import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Header.css";

// --- Components ---
import SurveyModal from "./SurveyModal"; // ★分離したコンポーネント

// --- Hooks & Constants ---
import { useLocaleNavigator } from "../hooks/useLocaleNavigator";
import { SUPPORTED_LANGUAGES, DEFAULT_LANG, FALLBACK_LANG } from "../constants/languages";

// --- Assets ---
import logoLargeWebp from '../assets/TOIMAP_logo.webp';
import logoLargePng from '../assets/TOIMAP_logo.png';
import logoSmallWebp from '../assets/TOIMAP_logo-small.webp';
import logoSmallPng from '../assets/TOIMAP_logo-small.png';
import { FaChevronDown, FaCommentDots } from 'react-icons/fa';
import { FaRegSmile, FaPaperPlane } from 'react-icons/fa';

// --- Menu Data (ボタンのラベルのみHeaderに残す) ---
const menuTranslations = {
    ja: {
        map: 'マップ', groupAbout: 'TOIMAPについて', about: 'TOIMAPとは', issue: '社会課題への挑戦',
        news: 'お知らせ', howto: 'トイレの使い方', groupBusiness: 'ビジネス・自治体の方',
        cases: '活用事例', partners: '協賛・パートナー', pricing: '料金プラン', contact: 'お問い合わせ', research: '知っトイレ（研究記事）',
        surveyBtn: '改善の提案',      // PC: 「評価」より「提案」の方が前向き
        surveyBtnShort: 'ご意見',     // Mobile: 「評価」より柔らかい「ご意見」「感想」

        surveyTitle: 'TOIMAPをより良くするために', // タイトルも協力的なニュアンスに
        surveyDesc: '使いにくい点や、欲しい機能があれば教えてください。',
    },
    en: {
        map: 'Map', groupAbout: 'About Us', about: 'About TOIMAP', issue: 'Social Issues',
        news: 'News', howto: 'How to Use', groupBusiness: 'For Business',
        cases: 'Case Studies', partners: 'Partnership', pricing: 'Pricing', contact: 'Contact', research: 'Research & Insights',
        surveyBtn: 'Feedback',
        surveyBtnShort: 'Feedback',
        surveyTitle: 'Help us improve TOIMAP',
        surveyDesc: 'Share your thoughts or report issues.',
    }
};

const Header = () => {
    const location = useLocation();
    const { changeLanguage, getCurrentLang } = useLocaleNavigator();
    const currentLang = getCurrentLang();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);

    // ★Stateは「開いているかどうか」だけ
    const [isSurveyOpen, setIsSurveyOpen] = useState(false);

    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsLangDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const t = menuTranslations[currentLang] || menuTranslations[FALLBACK_LANG];
    const text = (key) => t[key] || menuTranslations[FALLBACK_LANG][key];

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const closeAllMenus = () => { setIsMenuOpen(false); setIsLangDropdownOpen(false); };
    const handleLanguageSelect = (code) => {
        // もし現在がマップページ（/map/...）であれば、カスタムフックを使わずに直接URLを書き換える
        if (isMapPage) {
            const currentPath = location.pathname;
            const pathParts = currentPath.split('/');

            // 最後の要素が2文字の言語コードなら、それを選択された言語に置き換える
            if (pathParts[pathParts.length - 1].length === 2) {
                pathParts[pathParts.length - 1] = code;
            } else {
                // 言語コードがURLの最後に無ければ追加する
                pathParts.push(code);
            }

            // ページ遷移（強制リロードなしでReact Routerの遷移を使う場合は useNavigate をインポートして使用してください）
            window.location.href = pathParts.join('/');
        } else {
            // マップページ以外は通常通り useLocaleNavigator を使う
            changeLanguage(code);
        }

        closeAllMenus();
    };
    const getLink = (path) => currentLang === DEFAULT_LANG ? path : `${path}/${currentLang}`;
    const getHomeLink = () => currentLang === DEFAULT_LANG ? '/' : `/${currentLang}`;

    // マップ画面判定
    const isMapPage = location.pathname.startsWith('/map/') && !location.pathname.startsWith('/maplist');

    return (
        <>
            <header className="header">
                {/* ロゴ: マップ画面(モバイル)のときはクラス 'compact' を付与 */}
                <Link to={getHomeLink()} className={`logo ${isMapPage ? 'compact' : ''}`} onClick={closeAllMenus}>
                    <picture>
                        <source media="(max-width: 768px)" srcSet={logoSmallWebp} type="image/webp" />
                        <source srcSet={logoLargeWebp} type="image/webp" />
                        <source media="(max-width: 768px)" srcSet={logoSmallPng} />
                        <img src={logoLargePng} alt="TOIMAP" width="150" height="35" />
                    </picture>
                </Link>

                {/* ヘッダー直置きアンケートボタン (モバイル・マップ用) */}
                {isMapPage && (
                    <button
                        className="header-mobile-survey-btn"
                        onClick={() => setIsSurveyOpen(true)}
                    >
                        <FaCommentDots /> <span>{text('surveyBtnShort')}</span>
                    </button>
                )}

                <nav className={`nav ${isMenuOpen ? "open" : ""}`}>
                    <div className="nav-links-wrapper">
                        <Link to={getLink('/maplist')} onClick={closeAllMenus} className="nav-link main-link">{text('map')}</Link>

                        <div className="nav-dropdown-container">
                            <span className="nav-link dropdown-trigger">{text('groupAbout')} <FaChevronDown className="dropdown-icon" /></span>
                            <div className="nav-dropdown-menu">
                                <Link to={getLink('/about')} onClick={closeAllMenus} className="dropdown-item">{text('about')}</Link>
                                <Link to={getLink('/issue')} onClick={closeAllMenus} className="dropdown-item">{text('issue')}</Link>
                                <Link to={getLink('/article')} onClick={closeAllMenus} className="dropdown-item">{text('research')}</Link>
                                <Link to={getLink('/news')} onClick={closeAllMenus} className="dropdown-item">{text('news')}</Link>
                                {currentLang !== 'ja' && <Link to={getLink('/how-to-use-toilets')} onClick={closeAllMenus} className="dropdown-item">{text('howto')}</Link>}
                            </div>
                        </div>

                        <div className="nav-dropdown-container">
                            <span className="nav-link dropdown-trigger">{text('groupBusiness')} <FaChevronDown className="dropdown-icon" /></span>
                            <div className="nav-dropdown-menu">
                                <Link to={getLink('/cases')} onClick={closeAllMenus} className="dropdown-item">{text('cases')}</Link>
                                <Link to={getLink('/partners')} onClick={closeAllMenus} className="dropdown-item highlight-item">{text('partners')}</Link>
                                <Link to={getLink('/pricing')} onClick={closeAllMenus} className="dropdown-item">{text('pricing')}</Link>
                            </div>
                        </div>

                        <Link to={getLink('/contact')} onClick={closeAllMenus} className="nav-link contact-btn">{text('contact')}</Link>
                    </div>

                    <div className="custom-lang-dropdown" ref={dropdownRef}>
                        <button className={`lang-dropdown-btn ${isLangDropdownOpen ? 'active' : ''}`} onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}>
                            <span className="globe-icon">🌐 Language</span> <span className="arrow-icon">▼</span>
                        </button>
                        {isLangDropdownOpen && (
                            <ul className="lang-dropdown-list">
                                {SUPPORTED_LANGUAGES.map((lang) => (
                                    <li key={lang.code} onClick={() => handleLanguageSelect(lang.code)} className={currentLang === lang.code ? 'selected' : ''}>{lang.label}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                </nav>

                <div className="hamburger" onClick={toggleMenu}>
                    <div className={`bar ${isMenuOpen ? 'open' : ''}`} />
                    <div className={`bar ${isMenuOpen ? 'open' : ''}`} />
                    <div className={`bar ${isMenuOpen ? 'open' : ''}`} />
                </div>
            </header>

            {/* ★分離したSurveyModalコンポーネントを配置 */}
            <SurveyModal
                isOpen={isSurveyOpen}
                onClose={() => setIsSurveyOpen(false)}
                currentLang={currentLang}
            />
        </>
    );
};

export default Header;